// SceneDialog — the one modal shell the game uses.
//
// Built on the radix `Dialog` (focus trap, Escape, scroll lock, aria wiring),
// it fixes what every modal used to repeat: width, the surface, an optional art
// banner, an icon beside the title, and a footer row. A caller supplies content,
// not chrome. Extra props (`data-testid`, `appDom` attributes) land on the
// dialog element itself.

import { type VariantProps, cva } from 'class-variance-authority';
import type * as React from 'react';
import { cn } from '../../lib/utils.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog.js';

const sceneDialogVariants = cva(
  'max-h-[88vh] gap-3 overflow-y-auto border-2 border-border/60 bg-background/98 backdrop-blur-xl',
  {
    variants: {
      /** `sm` 24rem · `md` 28rem · `lg` 32rem */
      size: { sm: 'sm:max-w-sm', md: 'sm:max-w-md', lg: 'sm:max-w-lg' },
    },
    defaultVariants: { size: 'md' },
  },
);

export interface SceneDialogProps
  extends Omit<React.ComponentProps<typeof DialogContent>, 'title' | 'children'>,
    VariantProps<typeof sceneDialogVariants> {
  /** Defaults to open: callers mount the dialog only while it should show. */
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
  /** Called when the dialog is dismissed (Escape, overlay, X). */
  readonly onClose?: () => void;
  /** A `SceneImage` (usually `aspect="strip"`) above the header. */
  readonly media?: React.ReactNode;
  /** A glyph or sprite beside the title. */
  readonly icon?: React.ReactNode;
  readonly title: React.ReactNode;
  readonly description?: React.ReactNode;
  /** Buttons along the bottom edge. */
  readonly footer?: React.ReactNode;
  readonly children?: React.ReactNode;
}

function SceneDialog({
  open = true,
  onOpenChange,
  onClose,
  media,
  icon,
  title,
  description,
  footer,
  children,
  size,
  className,
  ...props
}: SceneDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange?.(next);
        if (!next) onClose?.();
      }}
    >
      <DialogContent
        data-slot="scene-dialog"
        className={cn(sceneDialogVariants({ size }), className)}
        {...props}
      >
        {media}
        <DialogHeader>
          <div className="flex items-center gap-3">
            {icon && <span className="flex shrink-0">{icon}</span>}
            <div className="flex min-w-0 flex-col gap-0.5">
              <DialogTitle className="truncate">{title}</DialogTitle>
              {description && <DialogDescription>{description}</DialogDescription>}
            </div>
          </div>
        </DialogHeader>
        {children}
        {footer && <DialogFooter>{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}

export { SceneDialog, sceneDialogVariants };
